import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Inventory,
  Diamond,
  AttachMoney
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getOldMetalStock, updateOldMetalStock } from '../redux/actions/oldMetalActions';

const OldMetalStock = () => {
  const dispatch = useDispatch();
  const { oldMetalStock, loading, error } = useSelector((state) => state.oldMetal);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [formData, setFormData] = useState({
    metal_type_id: '',
    weight: '',
    rate_per_gram: ''
  });

  useEffect(() => {
    dispatch(getOldMetalStock());
  }, [dispatch]);

  const handleOpenDialog = (stock = null) => {
    if (stock) {
      setEditingStock(stock);
      setFormData({
        metal_type_id: stock.metal_type_id,
        weight: stock.weight,
        rate_per_gram: stock.rate_per_gram
      });
    } else {
      setEditingStock(null);
      setFormData({
        metal_type_id: '',
        weight: '',
        rate_per_gram: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStock(null);
    setFormData({
      metal_type_id: '',
      weight: '',
      rate_per_gram: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.metal_type_id || !formData.weight || !formData.rate_per_gram) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await dispatch(updateOldMetalStock(formData));
      handleCloseDialog();
      dispatch(getOldMetalStock()); // Refresh the list
    } catch (error) {
      console.error('Error updating old metal stock:', error);
    }
  };

  const getMetalIcon = (metalType) => {
    switch (metalType) {
      case 'Gold':
        return <Diamond color="warning" />;
      case 'Silver':
        return <Inventory color="info" />;
      case 'Platinum':
        return <Diamond color="primary" />;
      default:
        return <Inventory />;
    }
  };

  const getMetalColor = (metalType) => {
    switch (metalType) {
      case 'Gold':
        return 'warning';
      case 'Silver':
        return 'info';
      case 'Platinum':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Old Metal Stock
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Update Stock
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Metal Type</TableCell>
                  <TableCell>Purity</TableCell>
                  <TableCell>Weight (g)</TableCell>
                  <TableCell>Rate/Gram</TableCell>
                  <TableCell>Total Value</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {oldMetalStock && oldMetalStock.length > 0 ? (
                  oldMetalStock.map((stock) => (
                    <TableRow key={stock.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getMetalIcon(stock.metalType?.metal_type)}
                          <Typography variant="body1" fontWeight="bold">
                            {stock.metalType?.metal_type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={stock.metalType?.purity} 
                          size="small" 
                          color={getMetalColor(stock.metalType?.metal_type)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {parseFloat(stock.weight).toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          ₹{parseFloat(stock.rate_per_gram).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          ₹{Math.round(stock.total_value)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(stock.last_updated).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(stock)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No old metal stock found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Update Stock Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStock ? 'Update Old Metal Stock' : 'Add Old Metal Stock'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Metal Type ID"
                value={formData.metal_type_id}
                onChange={(e) => handleInputChange('metal_type_id', e.target.value)}
                placeholder="Enter metal type ID"
                type="number"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Weight (g)"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                placeholder="0.000"
                type="number"
                inputProps={{ step: "0.001" }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate per Gram (₹)"
                value={formData.rate_per_gram}
                onChange={(e) => handleInputChange('rate_per_gram', e.target.value)}
                placeholder="0.00"
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            {formData.weight && formData.rate_per_gram && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Calculated Total Value:</strong> ₹{Math.round(parseFloat(formData.weight) * parseFloat(formData.rate_per_gram))}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingStock ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OldMetalStock;
